import { cx } from "@emotion/css";
import { BookOpen, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
} from "src/components/Default/LekUI/Accordion";
import { Filters } from "src/components/Default/LekUI/Filters";
import { useAiResponseContext } from "src/hooks/useAi";
import { useFontSize } from "src/hooks/useFontSize";
import useLocalStorage from "src/hooks/useLocalStorage";

type ContextFilter = "all" | "cited";

interface AiResponseContextProps {
	responseId: number;
}

const DEFAULT_FILTER: ContextFilter = "all";
const FILTER_STORAGE_KEY = "aiContextFilter";

const QUOTE_REGEX = /(<blockquote class="ai-context__quote">)([\s\S]*?)(<\/blockquote>)/g;

function decodeQuoteEntities(html: string): string {
	return html.replace(QUOTE_REGEX, (_match, open, content, close) => {
		const decoded = content
			.replace(/&lt;/g, "<")
			.replace(/&gt;/g, ">")
			.replace(/&quot;/g, '"')
			.replace(/&#39;/g, "'");
		return `${open}${decoded}${close}`;
	});
}

export function AiResponseContext({ responseId }: AiResponseContextProps) {
	const { t } = useTranslation();
	const { fontSizeClass } = useFontSize();
	const [isOpen, setIsOpen] = useState(false);
	const [filter, setFilter] = useLocalStorage(FILTER_STORAGE_KEY, DEFAULT_FILTER) as [
		ContextFilter,
		(value: ContextFilter) => void,
		{ removeItem: () => void },
	];

	const { data, isLoading } = useAiResponseContext(responseId, {
		enabled: isOpen,
		filter,
	});
	const chunks: Array<{ textHtml: string | null }> = data?.context ?? [];
	const renderableChunks = chunks.filter((chunk) => !!chunk.textHtml);

	const filterOptions = [
		{ value: "all" as ContextFilter, label: t("ai.context.filter.all", "Wszystkie") },
		{ value: "cited" as ContextFilter, label: t("ai.context.filter.cited", "Tylko cytowane") },
	];

	return (
		<Accordion expanded={isOpen} onChange={(_e, expanded) => setIsOpen(expanded)}>
			<AccordionSummary expandIcon={<ChevronDown className="icon-small" />}>
				<div className="flex items-center gap-8">
					<BookOpen className="icon-small" />
					<span className={fontSizeClass}>
						{t("ai.context.title", "Kontekst (źródła RAG)")}
					</span>
				</div>
			</AccordionSummary>
			<AccordionDetails>
				<div className="flex flex-col gap-16">
					<div className="flex items-center justify-end">
						<Filters<ContextFilter>
							options={filterOptions}
							value={filter}
							onChange={setFilter}
							defaultValue={DEFAULT_FILTER}
						/>
					</div>

					{isLoading && (
						<span className={cx(fontSizeClass, "text-grey text-center")}>
							{t("ai.context.loading", "Ładowanie...")}
						</span>
					)}
					{!isLoading && renderableChunks.length === 0 && (
						<span className={cx(fontSizeClass, "text-grey text-center")}>
							{t("ai.context.empty", "Brak kontekstu")}
						</span>
					)}
					{!isLoading && renderableChunks.length > 0 && (
						<div className={cx(fontSizeClass, "ai-context")}>
							<span className="ai-context__summary">
								{t("ai.context.count", "Fragmentów: {{count}}", {
									count: renderableChunks.length,
								})}
							</span>
							<div className="flex flex-col gap-12">
								{renderableChunks.map((chunk, index) => (
									<div
										key={index}
										dangerouslySetInnerHTML={{
											__html: decodeQuoteEntities(chunk.textHtml as string),
										}}
									/>
								))}
							</div>
						</div>
					)}
				</div>
			</AccordionDetails>
		</Accordion>
	);
}
